import {
  AdjustmentType,
  AdjustmentUnit,
  OrderItem,
  OrderItemStatus,
  OrderPaymentStatus,
} from '@oolio-group/domain';
import { useCurrency, useTranslation } from '@oolio-group/localization';
import {
  getDisplayAdjustmentValue,
  getPaymentAdjustmentValue,
} from '@oolio-group/order-helper';
import React, {
  useEffect,
  useMemo,
  useRef,
  Ref,
  useSyncExternalStore,
} from 'react';
import {
  FlatList,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import { ScreenName } from '../../common/enum';
import theme from '../../common/theme';
import { themeType } from '../../common/theme-colors';
import { useTheme } from '../../hooks/useTheme';
import { Branding } from '../../component/Branding/Branding';
import CartItem from '../../component/CartItem/CartItem';
import { useCart } from '../../hooks/cart/CartProvider';
import useBehaviorSubjectState from '../../hooks/rxjs/useSubjectState';
import { useCartSetActiveScreen } from '../../hooks/useCartNavigationHelper/useCartSwitcherEffect';
import { sessionSubject } from '../../state/sessionObservable';
import { usePreviousValue } from '@oolio-group/hooks';
import UpgradeModal from '../../component/Modals/UpgradeModal/UpgradeModal';
import { useModal } from '@oolio-group/rn-use-modal';
import { upsellComboStore } from '../../store/upsellCombo';

const renderItem = ({ item }: { item: OrderItem; index: number }) => {
  return <CartItem item={item} />;
};

const CartScreen: React.FC = () => {
  const { formatCurrency } = useCurrency();
  const { translate } = useTranslation();
  const { showModal, closeModal } = useModal();
  // Use reactive dimensions that update on window resize
  const { width, height } = useWindowDimensions();

  useCartSetActiveScreen(ScreenName.CART);
  const { value: session } = useBehaviorSubjectState(sessionSubject);
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const { order } = useCart();
  const { showUpsellComboModal } = useSyncExternalStore(
    upsellComboStore.subscribe,
    upsellComboStore.getSnapshot,
  );
  const flatListRef = useRef<FlatList<OrderItem>>(null);
  const totalTax = order.taxes?.reduce((sum, tax) => tax.amount + sum, 0);
  const discounts = order?.adjustments?.filter(
    adjustment => adjustment.adjustmentType == AdjustmentType.DISCOUNT,
  );
  const nonPaymentSurcharges = order.adjustments?.filter(
    adjustment =>
      adjustment.adjustmentType == AdjustmentType.SURCHARGE &&
      !adjustment.allowOnPaymentType,
  );

  const paymentSurcharges = order?.adjustments?.filter(
    surcharge => surcharge.allowOnPaymentType,
  );

  const totalPaymentSurcharge = useMemo(
    () =>
      (order?.payments || [])
        .filter(x => x.status === OrderPaymentStatus.COMPLETE)
        .reduce((acc, x) => acc + Number(x?.paymentSurcharge ?? 0), 0),
    [order?.payments],
  );

  const nonPaymentSurchargeAmount =
    (order?.surchargeAmount || 0) - totalPaymentSurcharge;

  const paymentValue = getPaymentAdjustmentValue(
    order.subTotal + nonPaymentSurchargeAmount || 0,
    order.adjustments || [],
  );

  const Discount = () => {
    return (
      <>
        {(discounts || []).map(discount => {
          const discountFormatted =
            (discount.name && discount.name !== ''
              ? `${discount.name} `
              : 'Discount ') +
            '(' +
            String(
              discount.adjustmentUnit === AdjustmentUnit.FLAT
                ? `${formatCurrency(discount.amount)}`
                : `${discount.amount}%`,
            ) +
            ')';
          return (
            <View key={discount.id} style={styles.rowStyle}>
              <Text style={styles.smallText}>{discountFormatted}</Text>
              <Text style={styles.smallText} testID="order-discount">
                {formatCurrency(
                  getDisplayAdjustmentValue(
                    order.subTotal,
                    order.adjustments ?? [],
                    discount,
                  ),
                )}
              </Text>
            </View>
          );
        })}
      </>
    );
  };

  const Surcharge = () => {
    return (
      <>
        {(nonPaymentSurcharges || []).map(surcharge => {
          const surchargeFormatted =
            (surcharge.name ?? 'Surcharge') +
            '(' +
            String(
              surcharge.adjustmentUnit === AdjustmentUnit.PERCENTAGE
                ? `${surcharge.amount}%`
                : `${formatCurrency(surcharge.amount)}`,
            ) +
            ')';
          return (
            <View key={surcharge.id} style={styles.rowStyle}>
              <Text style={styles.smallText}>{surchargeFormatted}</Text>
              <Text style={styles.smallText} testID="order-surcharge">
                {formatCurrency(
                  getDisplayAdjustmentValue(
                    order.subTotal,
                    order.adjustments ?? [],
                    surcharge,
                  ),
                )}
              </Text>
            </View>
          );
        })}
      </>
    );
  };

  const paymentSurchargeFormatted = useMemo(
    () =>
      (paymentSurcharges || [])
        .map(
          paymentSurcharge =>
            (paymentSurcharge.name ?? 'Surcharge') +
            '(' +
            String(
              paymentSurcharge.adjustmentUnit === AdjustmentUnit.PERCENTAGE
                ? `${paymentSurcharge.amount}%`
                : `${formatCurrency(paymentSurcharge.amount)}`,
            ) +
            ')',
        )
        .join(', '),
    [paymentSurcharges, formatCurrency],
  );

  const displayableOrderItems = useMemo(
    () =>
      order?.orderItems?.filter(
        item =>
          ![
            OrderItemStatus.CANCELLED,
            OrderItemStatus.VOID,
            OrderItemStatus.TRANSFERRED,
          ].includes(item.status),
      ),
    [order],
  );

  const scrollToEnd = () => {
    setTimeout(() => {
      flatListRef && flatListRef.current?.scrollToEnd();
    }, 100);
  };

  const previousLength = usePreviousValue(displayableOrderItems?.length);

  useEffect(() => {
    if (
      displayableOrderItems &&
      previousLength &&
      displayableOrderItems?.length > previousLength
    ) {
      scrollToEnd();
    }
  }, [displayableOrderItems, previousLength]);

  useEffect(() => {
    if (showUpsellComboModal) {
      showModal(<UpgradeModal />);
    } else {
      closeModal();
    }
  }, [showUpsellComboModal, showModal, closeModal]);

  return (
    <View style={styles.container}>
      <View style={[styles.content, { height }]}>
        <View style={styles.topView}>
          <View style={styles.titleView}>
            <Text style={styles.largeText}>
              {order.customer
                ? translate('cdsCartScreen.greetingUser', {
                    firstName: order.customer.firstName,
                    lastName: order.customer.lastName,
                  })
                : !!order.orderName
                ? order.orderName
                : translate('cdsCartScreen.yourOrder')}
            </Text>
          </View>
          {!!order.orderNote && (
            <View style={styles.noteView}>
              <Text style={styles.noteText} testID="order-note">
                {order.orderNote.trim()}
              </Text>
            </View>
          )}
          <FlatList
            ref={flatListRef as Ref<FlatList<OrderItem>>}
            data={displayableOrderItems}
            renderItem={renderItem}
          />
        </View>
        <View style={styles.bottomView}>
          <View style={styles.rowStyle}>
            <Text style={styles.smallText}>
              {translate('payment.subTotal')}
            </Text>
            <Text style={styles.smallText} testID="order-subTotal">
              {formatCurrency(order.subTotal ?? 0)}
            </Text>
          </View>
          {typeof totalTax === 'number' && totalTax > 0 && (
            <View style={styles.rowStyle}>
              <Text style={styles.smallText}>
                {translate('payment.totalTaxes')}
              </Text>
              <Text style={styles.smallText} testID="order-taxes">
                {formatCurrency(totalTax)}
              </Text>
            </View>
          )}
          {typeof order.discountAmount === 'number' &&
            order.discountAmount > 0 && <Discount />}
          {typeof nonPaymentSurchargeAmount === 'number' &&
            nonPaymentSurchargeAmount > 0 && <Surcharge />}
          {typeof paymentValue === 'number' && paymentValue > 0 && (
            <View style={styles.rowStyle}>
              <Text style={styles.smallText}>{paymentSurchargeFormatted}</Text>
              <Text style={styles.smallText} testID="order-paymentValue">
                {formatCurrency(paymentValue)}
              </Text>
            </View>
          )}
          <View style={styles.rowStyle}>
            <Text style={styles.largeText}>
              {translate('payment.totalDue')}
            </Text>
            <Text style={styles.largeText} testID="order-totalPaymentAmount">
              {formatCurrency(order.amountDue ?? 0)}
            </Text>
          </View>
        </View>
      </View>
      <Branding
        screenConfig={
          session?.device?.deviceProfile?.customerDisplay?.cartScreen
        }
        brandColor={session?.device?.deviceProfile?.customerDisplay?.brandColor}
        storeName={session?.currentStore?.name}
        width={width / 2}
        height={height}
      />
    </View>
  );
};

const makeStyles = (colors: themeType) =>
  StyleSheet.create({
    container: {
      flex: 1,
      flexDirection: 'row',
    },
    content: {
      flex: 1,
      overflow: 'hidden',
      backgroundColor: colors.elevation0,
    },
    image: {
      flex: 1,
      width: '100%',
      height: '100%',
      resizeMode: 'cover',
    },
    topView: {
      flex: 1,
      paddingLeft: theme.spacing.large,
      paddingRight: theme.spacing.medium,
    },
    titleView: {
      borderBottomWidth: 1,
      borderBottomColor: colors.borderColor,
      paddingBottom: theme.spacing.large,
      paddingTop: theme.spacing.large,
      alignItems: 'center',
    },
    bottomView: {
      paddingVertical: theme.spacing.medium,
      paddingHorizontal: theme.spacing.large,
      borderWidth: 1,
      backgroundColor: colors.elevation1,
      borderColor: colors.borderColor,
    },
    rowStyle: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    smallText: {
      lineHeight: 22,
      fontWeight: '500',
      color: colors.textLight,
      fontSize: theme.fontSize.medium,
    },
    largeText: {
      lineHeight: 40,
      fontWeight: '600',
      color: colors.text,
      fontSize: theme.fontSize.large,
    },
    noteText: {
      lineHeight: 22,
      fontWeight: '500',
      color: colors.text,
      fontSize: theme.fontSize.medium,
    },
    noteView: {
      backgroundColor: colors.orderNote,
      paddingVertical: theme.spacing.medium,
      paddingHorizontal: theme.spacing.large,
      marginVertical: theme.spacing.small,
      borderRadius: theme.spacing.small,
      alignItems: 'center',
    },
  });

export default CartScreen;
